import { useNavigate } from "react-router-dom";
import { Boxes } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { adminApi } from "@/lib/adminAuth";
import { formatFechaHora } from "@/lib/format";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/common/ErrorState";
import { useState } from "react";

export default function Embarques() {
  usePageTitle("Embarques");
  const navigate = useNavigate();
  const [clienteId, setClienteId] = useState("");

  const { data: clientes = [] } = useQuery({
    queryKey: ["admin-clientes-opciones"],
    queryFn: () => adminApi.get("/admin/clientes/opciones").then((r) => r.data),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-embarques", clienteId],
    queryFn: () =>
      adminApi.get("/admin/embarques", { params: { cliente_id: clienteId || undefined } }).then((r) => r.data),
  });

  return (
    <div className="mx-auto max-w-6xl px-8 py-8">
      <div>
        <h1 className="text-xl font-semibold text-[var(--tc-text)]">Embarques</h1>
        <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
          {data ? (
            `${data.total} embarque${data.total === 1 ? "" : "s"}`
          ) : (
            <span className="inline-block h-3.5 w-20 animate-pulse rounded bg-[var(--tc-border)]" />
          )}
        </p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2.5">
        <select
          data-testid={ADMIN.embarquesFilterCliente}
          value={clienteId}
          onChange={(e) => setClienteId(e.target.value)}
          className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]"
        >
          <option value="">Todos los clientes</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="space-y-2 p-4" data-testid="embarques-list-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full rounded-[var(--tc-radius)]" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState testId="admin-embarques-error" mensaje="No se pudo cargar el listado de embarques." onRetry={refetch} />
        ) : !data || data.resultados.length === 0 ? (
          <div data-testid={ADMIN.embarquesEmptyState} className="flex flex-col items-center py-16 text-center">
            <Boxes size={26} className="text-[var(--tc-state-pending)]" />
            <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">No hay embarques con esos filtros.</p>
          </div>
        ) : (
          <table className="w-full min-w-[760px] text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead>
              <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
                <th className="px-4 py-3">Booking</th>
                <th className="px-4 py-3">Pedido del cliente</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Unidades</th>
                <th className="px-4 py-3">Etapas</th>
                <th className="px-4 py-3">Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {data.resultados.map((e) => (
                <tr
                  key={e.booking}
                  data-testid={ADMIN.embarquesRow}
                  onClick={() => navigate(`/admin/embarques/${encodeURIComponent(e.booking)}`)}
                  className="cursor-pointer border-b border-[var(--tc-border)] last:border-0 transition-colors hover:bg-[var(--tc-surface)]"
                >
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[var(--tc-text)]">{e.booking}</td>
                  <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{e.pedido_cliente || "—"}</td>
                  <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{e.cliente?.nombre || "—"}</td>
                  <td className="px-4 py-3 font-medium text-[var(--tc-text)]">{e.total_unidades}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {e.etapas.map((et) => (
                        <span key={et.id} className="rounded-full bg-[var(--tc-accent-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--tc-accent)]">
                          {et.nombre}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs tabular-nums text-[var(--tc-text-secondary)]">{formatFechaHora(e.actualizado_en)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
